/**
 * Fees — what the platform charges, and where each number comes from.
 *
 * Every figure on this page used to be an environment variable or a constant compiled into
 * the API, which meant a price change was a redeploy and the current price was somewhere no
 * operator could read it. Both of those are wrong for a treasury decision: the naira moves,
 * a vendor changes what it charges us, and the answer has to be a number typed here and in
 * force on the next quote.
 *
 * ## The three-source rule, which is most of the page
 *
 * Every number in force comes from one of three places, and each row says which:
 *
 * - **Environment** — the value the deployment ships with. A knob nobody has touched keeps
 *   reading it, so a fresh install prices exactly as it always has.
 * - **Inherited** — the two instant-settlement knobs, unset, *track* the manual conversion
 *   fee and the house spread live. Change the conversion fee and instant settlement follows
 *   in the same breath.
 * - **Operator** — what someone typed here. Beats both, and stops tracking anything.
 *
 * **Clearing an override is not the same as typing today's number back in**, and the editor
 * keeps the two apart on purpose: "Track the conversion fee" (or "Use the environment
 * value") removes the override and the knob follows forever after, while typing `25` freezes
 * it at 25 even after the fee it used to track moves. A copied number is correct exactly
 * until somebody edits the value it was copied from, and the way that gets discovered is a
 * customer priced wrong.
 *
 * ## What a change does and does not touch
 *
 * A saved fee applies to the **next** quote. It never reaches a quote already issued: trading
 * and convert both store the fee on the quote row and execute against the stored figure, so
 * a customer who is looking at a confirm screen is charged what they were shown. That is
 * stated on the page as well as here, because it is the first thing an operator worries
 * about before touching a live price.
 *
 * ## Grouped, because eight rows in one table is an inventory rather than a structure
 *
 * An operator asking "what do we charge on bills?" had to read every label to find out that
 * two of them were about bills, and the two instant-settlement knobs sat next to the crypto
 * withdrawal fee with nothing to say they were a pair. `groupFees` places each knob by domain
 * and drops an empty group rather than printing a heading with nothing under it.
 *
 * The grouping is a *presentation* and changes no value, no key and no request. A knob this
 * console has never heard of still appears — under its prefix, or under a plainly labelled
 * "Other fees" — because a page that rendered only the keys it recognised would hide a live
 * fee, and a hidden fee is one nobody can answer a customer about. See `lib/feeGroups.ts`.
 *
 * Read on `rates:read` — every role holds it, and support answering "why did my swap cost
 * more than the app said?" needs to see the fee that priced it. Writes need `rates:write`; a
 * fee is a treasury call, the same species as a rate override.
 */
import { useState } from 'react';

import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type { AdminFee, FeeValueSource, SetFeeRequest } from '../contracts/types.js';
import { Button, Card, ErrorBox, Input, LoadingState } from '../components/ui.js';
import { IconAlert } from '../components/icons.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { groupFees } from '../lib/feeGroups.js';
import { relativeTime } from '../lib/format.js';

const SOURCE_LABELS: Readonly<Record<FeeValueSource, string>> = {
  env: 'Environment',
  inherited: 'Inherited',
  operator: 'Operator',
};

/**
 * The provenance chips are the deposit-rails chips, reused deliberately rather than given a
 * second set of class names. Both pages answer the same question about a different number —
 * "where did this figure come from?" — and two visual languages for one idea is how an
 * operator learns to read one page and misread the other. The mapping is stated here because
 * the class names carry the *rails* vocabulary: shipped default, somewhere else, a human.
 */
const SOURCE_CLASS: Readonly<Record<FeeValueSource, string>> = {
  env: 'rail-src--catalogue',
  inherited: 'rail-src--custodian',
  operator: 'rail-src--operator',
};

export function FeesPage(): JSX.Element {
  const { api, session } = useAuth();
  const canWrite = session !== null && adminCan(session.me.role, 'rates:write');
  const state = useAsync(() => api.listFees(), []);
  const [editing, setEditing] = useState<AdminFee | null>(null);

  if (state.loading) return <LoadingState label="Loading fees…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;
  if (state.data === null) return <ErrorBox message="No fees are available." />;

  const fees = state.data.fees;
  const byKey = new Map(fees.map((fee) => [fee.key, fee]));
  const groups = groupFees(fees);

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Fees</h1>
        <p className="page-head__sub">
          Everything the platform charges, in one place. A fee you have not set uses the shipped default. A change applies to the next quote only: anyone already on a confirm screen pays what they were shown.
          {!canWrite && ' Your role can view these but not change them.'}
        </p>
      </div>

      {groups.map((group) => (
        <Card key={group.domain.id} title={group.domain.label} flush>
          {/*
            The heading's own line, saying what the group prices. A section title alone tells
            an operator where a knob lives; this tells them whether it is the one they want.
          */}
          <p className="card__lead" style={{ padding: '0 var(--space-5)' }}>
            {group.domain.hint}
          </p>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Fee</th>
                <th>In force</th>
                <th>Falls back to</th>
                <th>Last changed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {group.fees.map((fee) => (
                <tr key={fee.key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{fee.label}</div>
                    <div className="faint mono" style={{ fontSize: 11.5 }}>
                      {fee.key}
                    </div>
                  </td>
                  <td>
                    <span className="mono" style={{ fontWeight: 600 }}>
                      {formatValue(fee.effectiveValue, fee.unit)}
                    </span>{' '}
                    <span className={`rail-src ${SOURCE_CLASS[fee.source]}`}>
                      {SOURCE_LABELS[fee.source]}
                    </span>
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {fallbackLabel(fee, byKey)}
                  </td>
                  <td className="faint" style={{ fontSize: 12 }}>
                    {fee.updatedAt === null ? 'Never' : relativeTime(fee.updatedAt)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(fee)}>
                      {canWrite ? 'Edit' : 'Details'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </Card>
      ))}

      {editing !== null && (
        <FeeEditor
          // Keyed on the knob so switching rows resets the draft rather than carrying one
          // fee's half-typed figure onto another.
          key={editing.key}
          fee={editing}
          parent={editing.inheritsFrom === null ? null : (byKey.get(editing.inheritsFrom) ?? null)}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            // Refetched rather than patched in place. Saving the conversion fee also moves
            // whatever is inheriting it, and a board that showed only the edited row change
            // would hide exactly the behaviour this page exists to make legible. Working the
            // inheritance out locally instead would put a second copy of the precedence rule
            // in the console, which is how a console ends up disagreeing with the quote.
            state.reload();
          }}
        />
      )}
    </>
  );
}

/** What a knob reads when nobody has set it, in words an operator can act on. */
function fallbackLabel(fee: AdminFee, byKey: ReadonlyMap<string, AdminFee>): JSX.Element {
  if (fee.inheritsFrom !== null) {
    const parent = byKey.get(fee.inheritsFrom);
    return (
      <>
        {parent === undefined ? fee.inheritsFrom : parent.label}
        {parent !== undefined && (
          <>
            , currently <span className="mono">{formatValue(parent.effectiveValue, parent.unit)}</span>
          </>
        )}
      </>
    );
  }
  return (
    <>
      <span className="mono">{fee.envVar ?? '–'}</span>
      {fee.envValue !== null && (
        <>
          {' = '}
          <span className="mono">{formatValue(fee.envValue, fee.unit)}</span>
        </>
      )}
    </>
  );
}

/**
 * The focused editor for one knob.
 *
 * The field has three states and the panel keeps them visibly distinct: falling back,
 * overridden to a value, or being edited. Handing the knob back to its fallback is a separate
 * control rather than "clear the box", because an empty box is ambiguous — it could mean
 * "remove the override" or "I have not typed it yet" — and the two do opposite things. The
 * save below therefore always sends an explicit `null` or an explicit value; there is no
 * request shape that means "I did not look".
 */
function FeeEditor({
  fee,
  parent,
  canWrite,
  onClose,
  onSaved,
}: {
  fee: AdminFee;
  /** The knob this one tracks, when it tracks one — so the panel can show what it reads. */
  parent: AdminFee | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const [draft, setDraft] = useState(fee.overrideValue ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = draft.trim();
  const malformed = trimmed.length > 0 && !/^\d{1,15}$/.test(trimmed);
  // Compared as BigInt, never as Number: the bill cap is kobo and a ceiling checked as a
  // float is a ceiling that can come out on the wrong side of itself.
  const tooLarge = trimmed.length > 0 && !malformed && BigInt(trimmed) > BigInt(fee.maxValue);
  const blocked = malformed || tooLarge;
  const changed = trimmed !== (fee.overrideValue ?? '');

  const fallbackText =
    fee.inheritsFrom === null
      ? `the environment value${fee.envValue === null ? '' : ` (${formatValue(fee.envValue, fee.unit)})`}`
      : `${parent === null ? fee.inheritsFrom : parent.label}${
          parent === null ? '' : ` (currently ${formatValue(parent.effectiveValue, parent.unit)})`
        }`;

  async function save(): Promise<void> {
    if (blocked) return;
    setSaving(true);
    setError(null);
    try {
      // Explicit `null` clears the override; a value sets it. Never today's effective figure
      // in place of `null` — that would freeze the knob at this moment's number and is the
      // exact drift this page is built to prevent.
      const body: SetFeeRequest = { value: trimmed.length === 0 ? null : trimmed };
      await api.setFee(fee.key, body);
      toast.success(
        `${fee.label} saved`,
        'In force on the next quote. Quotes already issued keep the fee they were priced with.',
      );
      onSaved();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      size="wide"
      title={fee.label}
      description={fee.description}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Close
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!canWrite || !changed || blocked}
            onClick={() => void save()}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="rail-edit">
        <div className="rail-edit__field">
          <label className="field__label" htmlFor="fee-value">
            {fee.unit === 'bps' ? 'Percentage' : 'Amount in naira'}
          </label>
          <p className="rail-edit__hint">
            {fee.unit === 'bps'
              ? `Typed in hundredths of a percent, so 100 means 1% and 25 means 0.25%. The most you can set is ${formatValue(fee.maxValue, fee.unit)}.`
              : `Typed in kobo, so 100 means ₦1.00. The most you can set is ${formatValue(fee.maxValue, fee.unit)}.`}{' '}
            Leave it empty to read {fallbackText}.
          </p>
          <Input
            id="fee-value"
            className="mono"
            inputMode="numeric"
            placeholder={`Reading ${fallbackText}`}
            value={draft}
            disabled={!canWrite || saving}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="rail-edit__foot">
            <span className={blocked ? 'rail-edit__bad' : 'faint'}>
              {malformed
                ? 'A whole number, with no decimal point.'
                : tooLarge
                  ? `Above the ceiling of ${fee.maxValue}.`
                  : trimmed.length > 0
                    ? 'Overridden. This number stays put even if what it used to read moves.'
                    : fee.inheritsFrom === null
                      ? 'Reading the environment. It follows the deployed value.'
                      : `Tracking ${parent === null ? fee.inheritsFrom : parent.label}. It follows whatever that is set to.`}
            </span>
            {trimmed.length > 0 && (
              <button
                type="button"
                className="pcred-linkbtn"
                disabled={!canWrite || saving}
                onClick={() => setDraft('')}
              >
                {fee.inheritsFrom === null
                  ? 'Use the environment value'
                  : `Track ${parent === null ? fee.inheritsFrom : parent.label}`}
              </button>
            )}
          </div>
        </div>

        {fee.inheritsFrom !== null && trimmed.length > 0 && (
          <div className="pcred-note" role="note">
            <IconAlert size={18} />
            <div>
              <strong>This stops tracking {parent === null ? fee.inheritsFrom : parent.label}.</strong>{' '}
              Once it carries its own number, changing that fee will no longer move this one:
              the point when you mean to, and a quiet mispricing when you do not.
            </div>
          </div>
        )}

        {error !== null && <ErrorBox message={error} />}

        <div className="rail-edit__meta faint">
          {fee.updatedAt === null ? (
            <>You have never changed this. It is using the built-in value.</>
          ) : (
            <span title={fee.updatedByAdminId ?? undefined}>
              Last changed {relativeTime(fee.updatedAt)}
              {fee.updatedByAdminId !== null && ` by admin …${fee.updatedByAdminId.slice(-6)}`}.
            </span>
          )}
        </div>
      </div>
    </Modal>
  );
}

/**
 * A stored integer, as an operator reads it.
 *
 * Basis points are rendered as a percentage alongside the raw count, because `25` means
 * nothing on its own and `0.25%` is the number a treasury desk actually reasons about. The
 * division is done on the *string* rather than by parsing to a float: two decimal places of a
 * basis-point count is exact arithmetic on digits, and a percentage that renders as
 * `0.2500000000000001` is worse than no percentage at all.
 */
function formatValue(value: string, unit: 'bps' | 'kobo'): string {
  /*
   * The percentage, and only the percentage.
   *
   * This used to read "0.25% (25 bps)". The percentage came first because the stored form on
   * its own made the page unreadable to anyone who does not know what a basis point is - and
   * the parenthesis was the same problem in smaller print, sitting on every row of a screen
   * whose whole job is telling an operator what they are charging.
   *
   * Nothing is lost. The stored number is what the operator types, and the field where they
   * type it explains the relationship in words: 100 means 1%. It has no business being
   * repeated beside every figure they only want to read.
   */
  return unit === 'bps' ? percentOf(value) : `₦${nairaOf(value)}`;
}

function splitMinor(value: string, places: number): string {
  const digits = value.replace(/^0+(?=\d)/, '');
  const padded = digits.padStart(places + 1, '0');
  const whole = padded.slice(0, padded.length - places);
  const fraction = padded.slice(padded.length - places);
  return `${whole}.${fraction}`;
}

/** `25` → `0.25%`. Two decimal places, because the stored number is hundredths of a percent. */
function percentOf(bps: string): string {
  return `${splitMinor(bps, 2).replace(/\.00$/, '')}%`;
}

/** `10000` → `100.00`, with thousands separators on the whole part only. */
function nairaOf(kobo: string): string {
  const [whole = '0', fraction = '00'] = splitMinor(kobo, 2).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${fraction}`;
}
